import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { compose } from 'recompose';
import { reduxForm, getFormValues, Field } from 'redux-form';
import { connect } from 'react-redux';

import { AddButton, TextInput, SelectInput, StatusChip, EmptyState, Modal } from '../../_Components';
import { UPDATE, CREATE, provider } from '../../_utils';
import {
    ColumnPicker,
    ReportSettingsFields,
    TARIF_OPTIONS,
    emptyQueues,
    mergeReportFields,
} from '../../reports/components/reportSettingsUi';
import './reports.css';

const isReportActive = (status) => status === 'Active';

const NewReportForm = () => (
    <Fragment>
        <Field name="name" label="Название" required component={TextInput} />
        <Field name="settings.tarif" label="Тарификация" options={TARIF_OPTIONS} component={SelectInput} />
    </Fragment>
);

const ReportsFunc = ({ formValues, change, isSaving, queues }) => {
    const [selected, setSelected] = useState(0);
    const [tab, setTab] = useState('settings');
    const [newReport, setNewReport] = useState(false);

    const campaigns = useMemo(
        () => (formValues && formValues.cards ? formValues.cards : []).map((card) => ({ id: card._id, name: card.name })),
        [formValues]
    );
    const reports = (formValues && formValues.reports) || [];
    const fields = useMemo(
        () => mergeReportFields(formValues && formValues.fields),
        [formValues]
    );

    useEffect(() => {
        if (selected >= reports.length) {
            setSelected(Math.max(0, reports.length - 1));
        }
    }, [reports.length, selected]);

    if (!formValues) {
        return null;
    }

    const open = () => setNewReport(!newReport);

    const save = async (report) => {
        if (!report) return;
        report.project = formValues._id;
        report.status = 'Active';
        if (!report.settings) report.settings = {};
        if (!report.settings.queues) report.settings.queues = emptyQueues();
        const { status, data } = await provider(CREATE, '/reports', { data: report });
        if (status === 200 && data) {
            const next = reports.concat([data]);
            change('reports', next);
            await provider(UPDATE, '/projects', {
                id: formValues._id,
                data: { ...formValues, reports: next },
            });
            setNewReport(false);
            setSelected(next.length - 1);
            setTab('settings');
        }
    };

    const report = reports[selected];
    const active = report && isReportActive(report.status);
    const screenCount = report && report.report && Array.isArray(report.report.report) ? report.report.report.length : 0;
    const xlsCount = report && report.report && Array.isArray(report.report.xls) ? report.report.xls.length : 0;
    const campaignId = report && report.settings && report.settings.campaign;
    const cardName = (campaigns.find((card) => String(card.id) === String(campaignId && campaignId._id ? campaignId._id : campaignId)) || {}).name;

    return (
        <Fragment>
            <div className="pc-fill pc-pr">
                <div className="pc-pr-panel">
                    <div className="pc-pr-phd">
                        <h2>Отчёты</h2>
                        <StatusChip tone="mono">{reports.length}</StatusChip>
                        <div className="pc-pr-phd__rt">
                            <AddButton handleAddClick={open} label="Добавить" variant="contained" />
                        </div>
                    </div>
                    <div className="pc-pr-pbd pc-pr-rlist">
                        {reports.length ? reports.map((row, index) => (
                            <button
                                type="button"
                                key={row._id || index}
                                className={`${index === selected ? 'is-cur' : ''} ${!isReportActive(row.status) || row.deleted ? 'is-off' : ''}`}
                                onClick={() => setSelected(index)}
                            >
                                <span className="t">{row.name}</span>
                                <StatusChip tone={isReportActive(row.status) ? 'ok' : 'danger'} dot>
                                    {isReportActive(row.status) ? 'Активен' : 'Отключён'}
                                </StatusChip>
                            </button>
                        )) : (
                            <EmptyState title="Нет отчётов" description="Добавьте отчёт, чтобы настроить кабинет клиента." />
                        )}
                    </div>
                </div>

                <div className="pc-pr-panel">
                    {report ? (
                        <Fragment>
                            <div className="pc-pr-phd">
                                <h2>{report.name || 'Отчёт'}</h2>
                                <StatusChip tone={active ? 'ok' : 'danger'} dot>
                                    {active ? 'Активен' : 'Отключён'}
                                </StatusChip>
                            </div>
                            <div className="pc-pr-subtabs" role="tablist">
                                <button type="button" role="tab" aria-selected={tab === 'settings'} onClick={() => setTab('settings')}>Настройки</button>
                                <button type="button" role="tab" aria-selected={tab === 'screen'} onClick={() => setTab('screen')}>
                                    Отчёт на экране<span className="c">{screenCount || ''}</span>
                                </button>
                                <button type="button" role="tab" aria-selected={tab === 'xls'} onClick={() => setTab('xls')}>
                                    Отчёт Excel<span className="c">{xlsCount || ''}</span>
                                </button>
                            </div>
                            <div className={`pc-pr-sub${tab === 'settings' ? ' is-on' : ''}`}>
                                <ReportSettingsFields
                                    prefix={`reports[${selected}].`}
                                    report={report}
                                    campaigns={campaigns}
                                    queuesList={queues}
                                    change={change}
                                    isSaving={isSaving}
                                />
                            </div>
                            <div className={`pc-pr-sub${tab === 'screen' ? ' is-on' : ''}`}>
                                <div className="pc-pr-wrap">
                                    <Field
                                        name={`reports[${selected}].report.report`}
                                        component={ColumnPicker}
                                        available={fields}
                                        kind="scr"
                                        cardName={cardName}
                                    />
                                </div>
                            </div>
                            <div className={`pc-pr-sub${tab === 'xls' ? ' is-on' : ''}`}>
                                <div className="pc-pr-wrap">
                                    <Field
                                        name={`reports[${selected}].report.xls`}
                                        component={ColumnPicker}
                                        available={fields}
                                        kind="xls"
                                        cardName={cardName}
                                        onCopyFromScreen={() => change(
                                            `reports[${selected}].report.xls`,
                                            (report.report && report.report.report) || []
                                        )}
                                    />
                                </div>
                            </div>
                        </Fragment>
                    ) : (
                        <EmptyState title="Выберите отчёт" description="Список слева — отчёты этого проекта." />
                    )}
                </div>
            </div>
            {newReport && (
                <Modal
                    title="Добавить отчёт"
                    open={newReport}
                    handleSaveClick={save}
                    handleCloseClick={open}
                    fields={<NewReportForm />}
                />
            )}
        </Fragment>
    );
};

const mapStateToProps = (state) => ({
    formValues: getFormValues('project')(state),
});

const enhance = compose(
    connect(mapStateToProps),
    reduxForm({
        form: 'project',
        destroyOnUnmount: false,
        forceUnregisterOnUnmount: true,
    })
);

export default enhance(ReportsFunc);
